/**
 * writeBack.ts — shared write-back helper.
 *
 * Called by BlockShell.handleBlur and useCaretInsert after DOM mutations
 * so that React state is updated in exactly one place.
 */
import { unchip, decodeEntities } from "./chipDom";
import { EmailBuilderActions } from "./useEmailBuilder";

/** Row fields that require index-based setRowText. */
const ROW_FIELDS = ["rlbl", "rval", "rlbl2", "rval2", "rraw", "nk", "nv", "nli", "fline", "hdrline"];

/**
 * Read the innerHTML of a data-el element, unchip it, and dispatch
 * the appropriate action to sync builder state.
 *
 * @param el      The contenteditable element carrying `data-el` + `data-f`.
 * @param blockId The block that owns this element.
 * @param actions The email builder actions object.
 */
export function writeBackFromEl(el: HTMLElement, blockId: string, actions: EmailBuilderActions): void {
	const dataF = el.getAttribute("data-f");
	if (!dataF) return;

	// unchip first (chips → ${var}), then decode entities (&nbsp;/&amp;/… → plain text)
	// so stored content never contains innerHTML artifacts that would render literally.
	const raw = decodeEntities(unchip(el.innerHTML));

	const fParts = dataF.split(":");
	const fieldBase = fParts[0];
	const rowIndex = fParts.length >= 2 ? parseInt(fParts[1], 10) : -1;

	writeValueToEl(el, blockId, raw, actions);
}

/**
 * Dispatch a PROVIDED value (already unchipped) for a data-el element — used when the
 * new value is computed without mutating the contentEditable DOM (e.g. drag-drop insert).
 * Mutating React-managed contentEditable children directly desyncs the vDOM and throws
 * "removeChild: node is not a child" on the next render, so we update state instead.
 */
export function writeValueToEl(el: HTMLElement, blockId: string, value: string, actions: EmailBuilderActions): void {
	const dataF = el.getAttribute("data-f");
	if (!dataF) return;

	const fParts = dataF.split(":");
	const fieldBase = fParts[0];
	const rowIndex = fParts.length >= 2 ? parseInt(fParts[1], 10) : -1;

	if (ROW_FIELDS.includes(fieldBase) && rowIndex >= 0) {
		actions.setRowText(blockId, fieldBase, rowIndex, value);
	} else {
		actions.setBlockData(blockId, { [fieldBase]: value });
	}
}
